/**
 * Claw9 Custom Hooks
 * React hooks for Claw9 Infrastructure API integration
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import * as workspacesApi from '../api/workspaces';
import * as filesApi from '../api/files';
import * as gatewayApi from '../api/gateway';
import * as healthApi from '../api/health';
import * as backupsApi from '../api/backups';
import * as templatesApi from '../api/templates';

// Demo mode - set to true when backend is not available
const USE_DEMO_MODE = true;

// Mock data for demo mode
const MOCK_WORKSPACES = [
    {
        bot_id: 'claw9',
        path: '/home/ubuntu/.openclaw/workspaces/claw9',
        modified_at: new Date().toISOString(),
        size_bytes: 15360,
        file_count: 5,
        files: ['SOUL.md', 'IDENTITY.md', 'AGENTS.md', 'SALES_KNOWLEDGE.md', 'knowledge_base.csv']
    },
    {
        bot_id: 'cloudmortgage',
        path: '/home/ubuntu/.openclaw/workspaces/cloudmortgage',
        modified_at: new Date(Date.now() - 86400000).toISOString(),
        size_bytes: 8192,
        file_count: 4,
        files: ['SOUL.md', 'IDENTITY.md', 'AGENTS.md', 'SALES_KNOWLEDGE.md']
    }
];

const MOCK_GATEWAY_STATUS = {
    running: true,
    uptime: new Date(Date.now() - 86400000).toISOString(),
    agent_count: 2,
    binding_count: 3,
    last_check: new Date().toISOString()
};

const MOCK_GATEWAY_CONFIG = {
    agents: {
        defaults: { model: { primary: 'gpt-4o' } },
        list: [
            { id: 'claw9', name: 'Claw9 Phone Shop', workspace: '/home/ubuntu/.openclaw/workspaces/claw9' },
            { id: 'cloudmortgage', name: 'CloudMortgage Bot', workspace: '/home/ubuntu/.openclaw/workspaces/cloudmortgage' }
        ]
    },
    bindings: [
        { agentId: 'claw9', match: { channel: 'zalo', accountId: 'default' } },
        { agentId: 'cloudmortgage', match: { channel: 'telegram', accountId: 'default' } }
    ],
    channels: {
        zalo: { enabled: true, botToken: '***REDACTED***' },
        telegram: { enabled: true, botToken: '***REDACTED***' }
    }
};

const MOCK_HEALTH = {
    healthy: true,
    ssh_connected: true,
    gateway_running: true,
    disk_ok: true,
    memory_ok: true,
    issues: []
};

const MOCK_METRICS = {
    cpu_percent: 25.5,
    memory_percent: 45.2,
    memory_used_mb: 1842,
    memory_total_mb: 4096,
    disk_percent: 62.1,
    disk_used_gb: 12.4,
    disk_total_gb: 20.0
};

const MOCK_BACKUPS = [
    {
        backup_id: 'claw9-20260321-143000',
        bot_id: 'claw9',
        filename: 'claw9-20260321-143000.tar.gz',
        size_bytes: 102400,
        created_at: new Date().toISOString()
    }
];

const MOCK_TEMPLATES = [
    { template_id: '_template', name: 'Default Template', description: 'Basic bot template' },
    { template_id: 'phone-shop', name: 'Phone Shop Bot', description: 'Template for phone retail bots' }
];

/**
 * Hook for managing workspaces
 */
export function useWorkspaces(options = {}) {
    const { autoFetch = true } = options;

    const [workspaces, setWorkspaces] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchWorkspaces = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                setWorkspaces(MOCK_WORKSPACES);
                return MOCK_WORKSPACES;
            }
            const result = await workspacesApi.listWorkspaces();
            setWorkspaces(result.data || []);
            return result.data;
        } catch (err) {
            console.log('[useWorkspaces] Using demo data:', err.message);
            setWorkspaces(MOCK_WORKSPACES);
            return MOCK_WORKSPACES;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const createWorkspace = useCallback(async (params) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                const newWorkspace = {
                    bot_id: params.botId || params.bot_id,
                    path: `/home/ubuntu/.openclaw/workspaces/${params.botId || params.bot_id}`,
                    modified_at: new Date().toISOString(),
                    size_bytes: 1024,
                    file_count: 5,
                    files: ['SOUL.md', 'IDENTITY.md', 'AGENTS.md', 'SALES_KNOWLEDGE.md', 'knowledge_base.csv']
                };
                setWorkspaces(prev => [...prev, newWorkspace]);
                return newWorkspace;
            }
            const result = await workspacesApi.createWorkspace(params);
            await fetchWorkspaces();
            return result.data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [fetchWorkspaces]);

    const deleteWorkspace = useCallback(async (botId) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                setWorkspaces(prev => prev.filter(w => w.bot_id !== botId));
                return;
            }
            await workspacesApi.deleteWorkspace(botId);
            setWorkspaces(prev => prev.filter(w => w.bot_id !== botId));
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (autoFetch) {
            fetchWorkspaces();
        }
    }, [autoFetch, fetchWorkspaces]);

    return {
        workspaces,
        isLoading,
        error,
        fetchWorkspaces,
        createWorkspace,
        deleteWorkspace,
        refresh: fetchWorkspaces
    };
}

/**
 * Hook for workspace files
 */
export function useWorkspaceFiles(botId) {
    const [files, setFiles] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const readFile = useCallback(async (filename) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                const mockContent = `# ${filename}\n\nThis is demo content for ${filename}`;
                setFiles(prev => ({ ...prev, [filename]: { content: mockContent, filename } }));
                return { content: mockContent, filename };
            }
            const result = await filesApi.readFile(botId, filename);
            setFiles(prev => ({ ...prev, [filename]: result.data }));
            return result.data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [botId]);

    const writeFile = useCallback(async (filename, content) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                setFiles(prev => ({ ...prev, [filename]: { content, filename } }));
                return { filename, content };
            }
            const result = await filesApi.writeFile(botId, filename, content);
            setFiles(prev => ({ ...prev, [filename]: { ...prev[filename], content } }));
            return result.data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [botId]);

    return {
        files,
        isLoading,
        error,
        readFile,
        writeFile
    };
}

/**
 * Hook for gateway status
 */
export function useGatewayStatus(pollInterval = 0) {
    const [status, setStatus] = useState(null);
    const [config, setConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const intervalRef = useRef(null);

    const fetchStatus = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                setStatus(MOCK_GATEWAY_STATUS);
                setConfig(MOCK_GATEWAY_CONFIG);
                return;
            }
            const [statusResult, configResult] = await Promise.all([
                gatewayApi.getGatewayStatus(),
                gatewayApi.getGatewayConfig()
            ]);
            setStatus(statusResult.data);
            setConfig(configResult.data);
        } catch (err) {
            console.log('[useGatewayStatus] Using demo data:', err.message);
            setStatus(MOCK_GATEWAY_STATUS);
            setConfig(MOCK_GATEWAY_CONFIG);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const restartGateway = useCallback(async (options) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                await new Promise(r => setTimeout(r, 1000));
                return { success: true };
            }
            await gatewayApi.restartGateway(options);
            setTimeout(fetchStatus, 2000);
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [fetchStatus]);

    const reloadConfig = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                return { success: true };
            }
            await gatewayApi.reloadGatewayConfig();
            await fetchStatus();
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [fetchStatus]);

    useEffect(() => {
        fetchStatus();

        if (pollInterval > 0) {
            intervalRef.current = setInterval(fetchStatus, pollInterval);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [fetchStatus, pollInterval]);

    return {
        status,
        config,
        isLoading,
        error,
        refresh: fetchStatus,
        restartGateway,
        reloadConfig
    };
}

/**
 * Hook for health metrics
 */
export function useHealthMetrics(pollInterval = 0) {
    const [health, setHealth] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const intervalRef = useRef(null);

    const fetchHealth = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                setHealth(MOCK_HEALTH);
                setMetrics(MOCK_METRICS);
                return;
            }
            const [healthResult, metricsResult] = await Promise.all([
                healthApi.getHealth(),
                healthApi.getMetrics()
            ]);
            setHealth(healthResult.data);
            setMetrics(metricsResult.data);
        } catch (err) {
            console.log('[useHealthMetrics] Using demo data:', err.message);
            setHealth(MOCK_HEALTH);
            setMetrics(MOCK_METRICS);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const triggerCheck = useCallback(async () => {
        await fetchHealth();
    }, [fetchHealth]);

    useEffect(() => {
        fetchHealth();

        if (pollInterval > 0) {
            intervalRef.current = setInterval(fetchHealth, pollInterval);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [fetchHealth, pollInterval]);

    return {
        health,
        metrics,
        isLoading,
        error,
        refresh: fetchHealth,
        triggerCheck
    };
}

/**
 * Hook for backups
 */
export function useBackups(options = {}) {
    const { botId, autoFetch = true } = options;

    const [backups, setBackups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchBackups = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                setBackups(MOCK_BACKUPS);
                return MOCK_BACKUPS;
            }
            const result = await backupsApi.listBackups({ botId });
            setBackups(result.data || []);
            return result.data;
        } catch (err) {
            console.log('[useBackups] Using demo data:', err.message);
            setBackups(MOCK_BACKUPS);
            return MOCK_BACKUPS;
        } finally {
            setIsLoading(false);
        }
    }, [botId]);

    const createBackup = useCallback(async (params) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                const newBackup = {
                    backup_id: `${params.botId}-${Date.now()}`,
                    bot_id: params.botId,
                    filename: `${params.botId}-${Date.now()}.tar.gz`,
                    size_bytes: 102400,
                    created_at: new Date().toISOString()
                };
                setBackups(prev => [newBackup, ...prev]);
                return newBackup;
            }
            const result = await backupsApi.createBackup(params);
            await fetchBackups();
            return result.data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [fetchBackups]);

    const deleteBackup = useCallback(async (backupId) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                setBackups(prev => prev.filter(b => b.backup_id !== backupId));
                return;
            }
            await backupsApi.deleteBackup(backupId);
            setBackups(prev => prev.filter(b => b.backup_id !== backupId));
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const restoreBackup = useCallback(async (backupId, options) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                return { success: true };
            }
            const result = await backupsApi.restoreBackup(backupId, options);
            return result.data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (autoFetch) {
            fetchBackups();
        }
    }, [autoFetch, fetchBackups]);

    return {
        backups,
        isLoading,
        error,
        fetchBackups,
        createBackup,
        deleteBackup,
        restoreBackup,
        refresh: fetchBackups
    };
}

/**
 * Hook for templates
 */
export function useTemplates(options = {}) {
    const { autoFetch = true } = options;

    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchTemplates = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                setTemplates(MOCK_TEMPLATES);
                return MOCK_TEMPLATES;
            }
            const result = await templatesApi.listTemplates();
            setTemplates(result.data || []);
            return result.data;
        } catch (err) {
            console.log('[useTemplates] Using demo data:', err.message);
            setTemplates(MOCK_TEMPLATES);
            return MOCK_TEMPLATES;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const createTemplate = useCallback(async (params) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                const newTemplate = {
                    template_id: params.templateId || params.template_id,
                    name: params.name,
                    description: params.description
                };
                setTemplates(prev => [...prev, newTemplate]);
                return newTemplate;
            }
            const result = await templatesApi.createTemplate(params);
            await fetchTemplates();
            return result.data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [fetchTemplates]);

    const deleteTemplate = useCallback(async (templateId) => {
        setIsLoading(true);
        setError(null);
        try {
            if (USE_DEMO_MODE) {
                setTemplates(prev => prev.filter(t => t.template_id !== templateId));
                return;
            }
            await templatesApi.deleteTemplate(templateId);
            setTemplates(prev => prev.filter(t => t.template_id !== templateId));
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (autoFetch) {
            fetchTemplates();
        }
    }, [autoFetch, fetchTemplates]);

    // Extract unique categories from templates
    const categories = [...new Set(templates.map(t => t.category).filter(Boolean))];

    return {
        templates,
        categories,
        isLoading,
        error,
        fetchTemplates,
        createTemplate,
        deleteTemplate,
        refresh: fetchTemplates
    };
}
